/**
 * Reorders two arrays to match each other based on a mapping logic.
 * Arrays can be in different order and of different length.
 * It also places non-matching items at the end of the array.
 *
 * @template T - The type of the items in the arrays.
 *
 * @param {Object} params - The parameters object.
 * @param {T[]} params.array1 - The first array to be reordered.
 * @param {T[]} params.array2 - The second array to be reordered.
 * @param {(item: T) => [string, T]} params.mappingLogic - A function that maps an item to a tuple containing a unique identifier and the item itself.
 *
 * @returns {[T[], T[]]} - The reordered arrays.
 *
 * @example
 * ```typescript
 * const array1 = [{ id: 'a', value: 1 }, { id: 'b', value: 2 }];
 * const array2 = [{ id: 'c', value: 3 }, { id: 'b', value: 3 }, { id: 'a', value: 4 }];
 *
 * const mappingLogic = (item: { id: string, value: number }) => [item.id, item];
 *
 * const [newArray1, newArray2] = reorderItemListsToMatchById({ array1, array2, mappingLogic });
 *
 * console.log(newArray1); // [{ id: 'a', value: 1 }, { id: 'b', value: 2 }]
 * console.log(newArray2); // [{ id: 'a', value: 4 }, { id: 'b', value: 3 }, { id: 'c', value: 3 }]
 * ```
 */
export const reorderItemListsToMatchById = <T>({
  array1,
  array2,
  mappingLogic
}: {
  array1: T[]
  array2: T[]
  mappingLogic: (item: T) => [string, T]
}) => {
  const itemMap1 = new Map(array1.map(mappingLogic))
  const itemMap2 = new Map(array2.map(mappingLogic))

  const orderedArray1: T[] = []
  const orderedArray2: T[] = []

  itemMap1.forEach((asset, assetId) => {
    if (itemMap2.has(assetId)) {
      orderedArray1.push(asset)
      orderedArray2.push(itemMap2.get(assetId))
      itemMap2.delete(assetId)
    } else {
      orderedArray1.push(asset)
    }
  })

  itemMap2.forEach(asset => {
    orderedArray2.push(asset)
  })

  return [orderedArray1, orderedArray2]
}
